import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GridApi } from 'ag-grid-community'
import styled from '@emotion/styled'
import { isNil } from 'lodash'
import {
  CollateralBasketType,
  NegotiationActions,
  NegotiationDetail,
  NegotiationFieldMeta,
  NegotiationUpdateAttributes,
  RepoType
} from 'api'
import { NotificationType, useModal } from 'view/shared/modal'
import { NumericInputField } from 'view/components/inputs'
import { LabelPosition, TextButton, TextButtonType } from 'view/components/buttons'
import { Locale, Translation } from 'locale'
import {
  canUpdateAttributes,
  getHaircutFromGlobalSettings,
  getNegotiatedValue,
  getRateFromGlobalSettings,
  isNonUSClearedNegotiation
} from '../shared/utils'
import { transformHaircutInput, transformRateInput } from 'view/shared/datagrid'
import { GlobalGridSettings } from 'store/grid/gridTypes'
import { GridActions, GridToolbarSettings } from 'store/grid'
import { allGridSettingsSelector } from 'selectors/gridSelectors'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'
import { userPreferenceSelector } from 'selectors/userPreferenceSelectors'

export interface BulkPriceInputProps {
  gridApi: GridApi
  toolbarSettings: GridToolbarSettings
  onToolbarSettingsChanged: (toolbarSettings: GridToolbarSettings) => unknown
}

export const CustomNumericInput = styled(NumericInputField)`
  margin-right: 10px;
  .field-input {
    width: 60px;
  }
`

export const BulkPriceWrapper = styled.div`
  display: flex;
  margin-right: 20px;
`

export const BulkPriceInput: React.FunctionComponent<BulkPriceInputProps> = ({ gridApi }) => {
  const { showDialog } = useModal()
  const dispatch = useDispatch()
  const gridSettings = useSelector(allGridSettingsSelector)
  const isAdmin = useSelector(isAdminGatewayUserSelector)
  const userPreference = useSelector(userPreferenceSelector)

  if (isAdmin) {
    return null
  }

  const onSaveSettings = (partialSettings: Partial<Pick<GlobalGridSettings, 'bid' | 'bidHaircut' | 'offer' | 'offerHaircut'>>) => {
    dispatch(
      GridActions.saveGlobalGridSettings({
        bid: gridSettings.bid,
        bidHaircut: gridSettings.bidHaircut,
        offer: gridSettings.offer,
        offerHaircut: gridSettings.offerHaircut,
        ...partialSettings
      })
    )
  }

  const handleApply = async () => {
    const negotiationDetails: NegotiationDetail[] = gridApi.getSelectedRows()

    const updateAttributesList: NegotiationUpdateAttributes[] = negotiationDetails
      .filter(
        negotiationDetail =>
          canUpdateAttributes(negotiationDetail) && negotiationDetail.negotiation.repoType !== RepoType.STOCK_BORROWING_LENDING
      )
      .map(negotiationDetail => {
        const rate = getRateFromGlobalSettings(negotiationDetail, gridSettings)
        const haircut = getHaircutFromGlobalSettings(negotiationDetail, gridSettings)
        const nonUSClearedNegotiation = isNonUSClearedNegotiation(negotiationDetail)
        const isBasketIneligible =
          negotiationDetail.productSector?.basketType &&
          [CollateralBasketType.GC_BESPOKE, CollateralBasketType.GC_PLUS].includes(negotiationDetail.productSector?.basketType)
        return {
          negotiationId: negotiationDetail.negotiation.negotiationId,
          updatedValues: {
            [NegotiationFieldMeta.RATE]: isNil(rate) ? undefined : transformRateInput(rate, negotiationDetail.referenceRate),
            [NegotiationFieldMeta.HAIRCUT]:
              isNil(haircut) || isBasketIneligible || nonUSClearedNegotiation
                ? undefined
                : transformHaircutInput(haircut, userPreference.haircutSign, false, negotiationDetail.direction)
          },
          updatedRawValues: {
            [NegotiationFieldMeta.RATE]: isNil(rate) ? undefined : rate,
            [NegotiationFieldMeta.HAIRCUT]: isNil(haircut) || isBasketIneligible || nonUSClearedNegotiation ? undefined : haircut
          },
          meta: {
            haircutSign: userPreference.haircutSign
          }
        }
      })

    if (updateAttributesList.length === 0) {
      return
    }

    const hasExistingRate = negotiationDetails.some(negotiationDetail => !isNil(getNegotiatedValue(negotiationDetail.negotiation.rate)))
    const hasExistingHaircut = negotiationDetails.some(
      negotiationDetail => !isNil(getNegotiatedValue(negotiationDetail.negotiation.haircut))
    )
    const ownOrder = negotiationDetails.some(negotiationDetail => negotiationDetail.ownOrder)

    if (
      ((!isNil(gridSettings.bid) || !isNil(gridSettings.offer)) && hasExistingRate) ||
      ((!isNil(gridSettings.bidHaircut) || !isNil(gridSettings.offerHaircut)) && hasExistingHaircut)
    ) {
      const dialogArgs = {
        type: NotificationType.Confirm,
        title: Translation.TC_BULK_PRICE,
        message: Translation.SC_BULK_PRICE_CONFIRMATION,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        cancelButton: Translation.TC_BUTTON_NO,
        defaultButton: Translation.TC_BUTTON_YES
      }
      const dialogResult = await showDialog(dialogArgs)

      if (dialogResult === Translation.TC_BUTTON_YES) {
        dispatch(NegotiationActions.updateValuesBulk(updateAttributesList, ownOrder))
      }
    } else {
      dispatch(NegotiationActions.updateValuesBulk(updateAttributesList, ownOrder))
    }
  }

  return (
    <BulkPriceWrapper>
      <CustomNumericInput
        dataTestId={'QuickPrice-TextInputField-Bid'}
        label={Locale.translate(Translation.TC_BID)}
        value={gridSettings.bid}
        onUpdate={(value: number | null) => onSaveSettings({ bid: value })}
        labelPosition={LabelPosition.Left}
      />
      <CustomNumericInput
        dataTestId={'QuickPrice-TextInputField-BidHaircut'}
        label={Locale.translate(Translation.TC_BID_HAIRCUT)}
        value={gridSettings.bidHaircut}
        onUpdate={(value: number | null) => onSaveSettings({ bidHaircut: value })}
        labelPosition={LabelPosition.Left}
      />
      <CustomNumericInput
        dataTestId={'QuickPrice-TextInputField-Offer'}
        label={Locale.translate(Translation.TC_OFFER)}
        value={gridSettings.offer}
        onUpdate={(value: number | null) => onSaveSettings({ offer: value })}
        labelPosition={LabelPosition.Left}
      />
      <CustomNumericInput
        dataTestId={'QuickPrice-TextInputField-OfferHaircut'}
        label={Locale.translate(Translation.TC_OFFER_HAIRCUT)}
        value={gridSettings.offerHaircut}
        onUpdate={(value: number | null) => onSaveSettings({ offerHaircut: value })}
        labelPosition={LabelPosition.Left}
      />
      <TextButton dataTestId={'QuickPrice-TextButton-Price'} type={TextButtonType.Primary} onClick={handleApply}>
        {Locale.translate(Translation.TC_PRICE)}
      </TextButton>
    </BulkPriceWrapper>
  )
}
